/**
 * Client-side resume document generator — 9 ATS-friendly templates.
 * Produces styled PDF (jsPDF) and DOCX (jszip) matching the on-screen template selection.
 */

import { jsPDF } from 'jspdf';
import JSZip from 'jszip';

import {
  parseResumeSections,
  sortSectionsByCanonicalOrder,
  normalizeSectionTitles,
} from '../modules/dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import type { ResumeSection } from '../modules/dashboard/components/AIResumeOptimizer/utils/parseResumeSections';

import { normalizeResumeText } from './pdfTextSanitizer';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TemplateId =
  | 'blue-classic' | 'blue-modern' | 'blue-minimal'
  | 'navy-compact' | 'black-bold' | 'black-clean' | 'black-serif'
  | 'slate-pro' | 'mono-clean'
  | 'soft-white' | 'warm-cream' | 'sage-light' | 'pearl-rose';

type RGB = { r: number; g: number; b: number };
type SectionHeaderStyle = 'filled-bar' | 'text-underline' | 'text-double-underline';
type DocFont = 'helvetica' | 'times' | 'courier';

interface TemplatePalette {
  accentBar: RGB | null;
  headerBg: RGB | null;
  headerText: RGB;
  headerBorderColor: RGB;
  /** Light tint behind section titles in PDF/DOCX (matches on-screen preview) */
  sectionHeaderFillBg: RGB | null;
  sectionTitleColor: RGB;
  nameColor: RGB;
  jobTitleColor: RGB;
  bodyText: RGB;
  bulletColor: RGB;
  companyHeaderColor: RGB;
  roleTitleColor: RGB;
  dividerColor: RGB;
  sectionHeaderStyle: SectionHeaderStyle;
  nameAlign: 'left' | 'center';
  font: DocFont;
  docxFont: string;
  docxAccentHex: string;
  docxBodyHex: string;
  docxHeaderBgHex: string | null;
  docxHeaderTextHex: string;
}

const rgb = (r: number, g: number, b: number): RGB => ({ r, g, b });

/** Sans-serif DOCX/PDF body font — matches B2C app (--ds-font-sans). */
const DOCX_FONT_SANS = 'Lexend';

// ─── Template palettes ────────────────────────────────────────────────────────

const PALETTES: Record<TemplateId, TemplatePalette> = {
  'blue-classic': {
    accentBar:          rgb(30, 58, 95),
    headerBg:           rgb(30, 58, 95),
    headerText:         rgb(224, 242, 254),
    headerBorderColor:  rgb(147, 197, 253),
    sectionHeaderFillBg: rgb(219, 234, 254),
    sectionTitleColor:  rgb(30, 58, 95),
    nameColor:          rgb(15, 30, 54),
    jobTitleColor:      rgb(37, 99, 235),
    bodyText:           rgb(15, 30, 54),
    bulletColor:        rgb(29, 78, 216),
    companyHeaderColor: rgb(15, 30, 54),
    roleTitleColor:     rgb(37, 99, 235),
    dividerColor:       rgb(37, 99, 235),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '1E3A5F', docxBodyHex: '0F1E36',
    docxHeaderBgHex: 'DBEAFE', docxHeaderTextHex: '1E3A5F',
  },
  'blue-modern': {
    accentBar:          rgb(29, 78, 216),
    headerBg:           rgb(219, 234, 254),
    headerText:         rgb(30, 58, 95),
    headerBorderColor:  rgb(191, 219, 254),
    sectionHeaderFillBg: rgb(239, 246, 255),
    sectionTitleColor:  rgb(30, 58, 95),
    nameColor:          rgb(12, 26, 46),
    jobTitleColor:      rgb(29, 78, 216),
    bodyText:           rgb(12, 26, 46),
    bulletColor:        rgb(29, 78, 216),
    companyHeaderColor: rgb(12, 26, 46),
    roleTitleColor:     rgb(29, 78, 216),
    dividerColor:       rgb(29, 78, 216),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '1D4ED8', docxBodyHex: '0C1A2E',
    docxHeaderBgHex: 'EFF6FF', docxHeaderTextHex: '1E40AF',
  },
  'blue-minimal': {
    accentBar:          null,
    headerBg:           null,
    headerText:         rgb(29, 78, 216),
    headerBorderColor:  rgb(37, 99, 235),
    sectionHeaderFillBg: null,
    sectionTitleColor:  rgb(29, 78, 216),
    nameColor:          rgb(30, 41, 59),
    jobTitleColor:      rgb(37, 99, 235),
    bodyText:           rgb(30, 41, 59),
    bulletColor:        rgb(37, 99, 235),
    companyHeaderColor: rgb(30, 41, 59),
    roleTitleColor:     rgb(37, 99, 235),
    dividerColor:       rgb(37, 99, 235),
    sectionHeaderStyle: 'text-underline',
    nameAlign: 'center', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '1D4ED8', docxBodyHex: '1E293B',
    docxHeaderBgHex: null, docxHeaderTextHex: '1D4ED8',
  },
  'navy-compact': {
    accentBar:          rgb(10, 35, 66),
    headerBg:           rgb(10, 35, 66),
    headerText:         rgb(186, 230, 253),
    headerBorderColor:  rgb(191, 219, 254),
    sectionHeaderFillBg: rgb(239, 246, 255),
    sectionTitleColor:  rgb(10, 35, 66),
    nameColor:          rgb(13, 27, 46),
    jobTitleColor:      rgb(29, 78, 216),
    bodyText:           rgb(13, 27, 46),
    bulletColor:        rgb(29, 78, 216),
    companyHeaderColor: rgb(13, 27, 46),
    roleTitleColor:     rgb(29, 78, 216),
    dividerColor:       rgb(29, 78, 216),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '0A2342', docxBodyHex: '0D1B2E',
    docxHeaderBgHex: 'EFF6FF', docxHeaderTextHex: '1E3A5F',
  },
  'black-bold': {
    accentBar:          null,
    headerBg:           rgb(17, 24, 39),
    headerText:         rgb(249, 250, 251),
    headerBorderColor:  rgb(209, 213, 219),
    sectionHeaderFillBg: rgb(243, 244, 246),
    sectionTitleColor:  rgb(17, 24, 39),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(75, 85, 99),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(17, 24, 39),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(75, 85, 99),
    dividerColor:       rgb(17, 24, 39),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '111827', docxBodyHex: '111827',
    docxHeaderBgHex: 'F3F4F6', docxHeaderTextHex: '111827',
  },
  'black-clean': {
    accentBar:          null,
    headerBg:           rgb(31, 41, 55),
    headerText:         rgb(249, 250, 251),
    headerBorderColor:  rgb(229, 231, 235),
    sectionHeaderFillBg: rgb(249, 250, 251),
    sectionTitleColor:  rgb(31, 41, 55),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(55, 65, 81),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(55, 65, 81),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(55, 65, 81),
    dividerColor:       rgb(31, 41, 55),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '1F2937', docxBodyHex: '111827',
    docxHeaderBgHex: 'F9FAFB', docxHeaderTextHex: '1F2937',
  },
  'black-serif': {
    accentBar:          null,
    headerBg:           null,
    headerText:         rgb(17, 24, 39),
    headerBorderColor:  rgb(31, 41, 55),
    sectionHeaderFillBg: null,
    sectionTitleColor:  rgb(17, 24, 39),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(55, 65, 81),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(55, 65, 81),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(55, 65, 81),
    dividerColor:       rgb(31, 41, 55),
    sectionHeaderStyle: 'text-double-underline',
    nameAlign: 'center', font: 'times', docxFont: 'Times New Roman',
    docxAccentHex: '111827', docxBodyHex: '111827',
    docxHeaderBgHex: null, docxHeaderTextHex: '111827',
  },
  'slate-pro': {
    accentBar:          rgb(71, 85, 105),
    headerBg:           rgb(51, 65, 85),
    headerText:         rgb(241, 245, 249),
    headerBorderColor:  rgb(226, 232, 240),
    sectionHeaderFillBg: rgb(241, 245, 249),
    sectionTitleColor:  rgb(51, 65, 85),
    nameColor:          rgb(30, 41, 59),
    jobTitleColor:      rgb(71, 85, 105),
    bodyText:           rgb(30, 41, 59),
    bulletColor:        rgb(71, 85, 105),
    companyHeaderColor: rgb(30, 41, 59),
    roleTitleColor:     rgb(71, 85, 105),
    dividerColor:       rgb(71, 85, 105),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '334155', docxBodyHex: '1E293B',
    docxHeaderBgHex: 'F1F5F9', docxHeaderTextHex: '334155',
  },
  'mono-clean': {
    accentBar:          null,
    headerBg:           null,
    headerText:         rgb(17, 24, 39),
    headerBorderColor:  rgb(17, 24, 39),
    sectionHeaderFillBg: null,
    sectionTitleColor:  rgb(17, 24, 39),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(55, 65, 81),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(55, 65, 81),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(55, 65, 81),
    dividerColor:       rgb(17, 24, 39),
    sectionHeaderStyle: 'text-double-underline',
    nameAlign: 'center', font: 'courier', docxFont: 'Courier New',
    docxAccentHex: '111827', docxBodyHex: '111827',
    docxHeaderBgHex: null, docxHeaderTextHex: '111827',
  },
  'soft-white': {
    accentBar:          null,
    headerBg:           rgb(249, 250, 251),
    headerText:         rgb(55, 65, 81),
    headerBorderColor:  rgb(156, 163, 175),
    sectionHeaderFillBg: null,
    sectionTitleColor:  rgb(55, 65, 81),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(75, 85, 99),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(107, 114, 128),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(75, 85, 99),
    dividerColor:       rgb(156, 163, 175),
    sectionHeaderStyle: 'text-underline',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '374151', docxBodyHex: '111827',
    docxHeaderBgHex: null, docxHeaderTextHex: '374151',
  },
  'warm-cream': {
    accentBar:          null,
    headerBg:           rgb(245, 240, 232),
    headerText:         rgb(68, 55, 43),
    headerBorderColor:  rgb(196, 168, 130),
    sectionHeaderFillBg: null,
    sectionTitleColor:  rgb(68, 55, 43),
    nameColor:          rgb(28, 25, 23),
    jobTitleColor:      rgb(180, 83, 9),
    bodyText:           rgb(28, 25, 23),
    bulletColor:        rgb(196, 168, 130),
    companyHeaderColor: rgb(28, 25, 23),
    roleTitleColor:     rgb(180, 83, 9),
    dividerColor:       rgb(196, 168, 130),
    sectionHeaderStyle: 'text-underline',
    nameAlign: 'left', font: 'times', docxFont: 'Cambria',
    docxAccentHex: '44372B', docxBodyHex: '1C1917',
    docxHeaderBgHex: null, docxHeaderTextHex: '44372B',
  },
  'sage-light': {
    accentBar:          rgb(45, 90, 45),
    headerBg:           rgb(240, 247, 240),
    headerText:         rgb(45, 90, 45),
    headerBorderColor:  rgb(134, 184, 134),
    sectionHeaderFillBg: rgb(240, 247, 240),
    sectionTitleColor:  rgb(45, 90, 45),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(45, 90, 45),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(134, 184, 134),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(45, 90, 45),
    dividerColor:       rgb(134, 184, 134),
    sectionHeaderStyle: 'filled-bar',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '2D5A2D', docxBodyHex: '111827',
    docxHeaderBgHex: 'F0F7F0', docxHeaderTextHex: '2D5A2D',
  },
  'pearl-rose': {
    accentBar:          null,
    headerBg:           rgb(253, 242, 243),
    headerText:         rgb(90, 32, 40),
    headerBorderColor:  rgb(216, 122, 136),
    sectionHeaderFillBg: null,
    sectionTitleColor:  rgb(90, 32, 40),
    nameColor:          rgb(17, 24, 39),
    jobTitleColor:      rgb(216, 122, 136),
    bodyText:           rgb(17, 24, 39),
    bulletColor:        rgb(216, 122, 136),
    companyHeaderColor: rgb(17, 24, 39),
    roleTitleColor:     rgb(216, 122, 136),
    dividerColor:       rgb(216, 122, 136),
    sectionHeaderStyle: 'text-underline',
    nameAlign: 'left', font: 'helvetica', docxFont: DOCX_FONT_SANS,
    docxAccentHex: '5A2028', docxBodyHex: '111827',
    docxHeaderBgHex: null, docxHeaderTextHex: '5A2028',
  },
};

const DEFAULT_TEMPLATE: TemplateId = 'black-bold';

const LEXEND_REGULAR_URL =
  'https://cdn.jsdelivr.net/npm/@fontsource/lexend@5.2.5/files/lexend-latin-400-normal.ttf';
const LEXEND_BOLD_URL =
  'https://cdn.jsdelivr.net/npm/@fontsource/lexend@5.2.5/files/lexend-latin-700-normal.ttf';
const LEXEND_EMBED_REGULAR_GUID = '{6A3B9E0F-2C8D-41AF-9E3B-1D7C4A8F2E05}';
const LEXEND_EMBED_BOLD_GUID = '{7B4C0F1A-3D9E-52BC-AF4C-2E8D5B9F3F16}';

let cachedLexendRegular: Uint8Array | null = null;
let cachedLexendBold: Uint8Array | null = null;

async function loadLexendFontBytes(): Promise<{ regular: Uint8Array; bold: Uint8Array }> {
  if (!cachedLexendRegular || !cachedLexendBold) {
    const [regularRes, boldRes] = await Promise.all([
      fetch(LEXEND_REGULAR_URL),
      fetch(LEXEND_BOLD_URL),
    ]);
    if (!regularRes.ok || !boldRes.ok) {
      throw new Error('Failed to load Lexend fonts for DOCX export');
    }
    cachedLexendRegular = new Uint8Array(await regularRes.arrayBuffer());
    cachedLexendBold = new Uint8Array(await boldRes.arrayBuffer());
  }
  return { regular: cachedLexendRegular, bold: cachedLexendBold };
}

/** OOXML obfuscated font embedding — XOR first 32 bytes with GUID hex. */
function obfuscateFontForDocx(data: Uint8Array, guid: string): Uint8Array {
  const out = new Uint8Array(data);
  const guidHex = guid.replace(/[-{}]/g, '');
  for (let i = 0; i < 32 && i < out.length; i++) {
    out[i] ^= parseInt(guidHex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function usesEmbeddedLexend(fontName: string): boolean {
  return fontName === DOCX_FONT_SANS;
}

// ─── Section bucket order ─────────────────────────────────────────────────────

const BODY_BUCKETS = [
  'Professional Summary', 'Work Experience', 'Education',
  'Skills', 'Languages', 'Languages & Frameworks', 'Certifications',
  'Awards', 'Projects', 'Publications', 'Research',
  'Training & Development', 'Leadership & Activities',
  'Affiliations', 'Hobbies & Interests', 'Additional Information', 'References',
] as const;

// ─── Work-Experience line classification ──────────────────────────────────────

/**
 * Line types inside Work Experience (and similar chronological sections):
 * - company  : employer header with a date range — bold large
 * - role     : job title line (right after company or another role line) — bold medium
 * - bullet   : already starts with bullet char — indented bullet
 * - body     : responsibility sentence without bullet char — auto-bulleted + indented
 */
type WELineType = 'company' | 'role' | 'bullet' | 'body';

/** True when the line contains a recognisable date range (company/job header). */
function hasDateRange(line: string): boolean {
  // Matches: mm/yyyy, yyyy-mm (ISO month), or yyyy on each side; end can be "present"/"current"/"till date"
  return /\b(\d{1,2}\/\d{4}|\d{4}-\d{2}|\d{4})\s*[-–—]\s*(till\s*date|present|current|\d{1,2}\/\d{4}|\d{4}-\d{2}|\d{4})/i.test(line);
}

/** True when line contains a | separator (one or more) in "Company | Date" or "Company || Date" format. */
function hasPipeSeparator(line: string): boolean {
  const t = line.trim();
  return t.length >= 5 && t.length < 200 && /[A-Za-z].*\|.+/.test(t) && (hasDateRange(t) || /\d{4}/.test(t));
}

/**
 * When a single line contains both company+date and role (e.g. "Productboard || Jan 2016 - Mar 2019 Technical Manager - Product"),
 * split so the PDF can render company/date on one line and role on the next, matching the in-app preview layout.
 */
function splitCompanyDateAndRole(line: string): { company: string; role?: string } {
  const t = line.trim();
  if (!t || t.length < 20) return { company: t };
  // Match date range end: Present, Current, or year/month pattern, then optional trailing role text
  const dateEndMatch = t.match(
    /^(.+?(?:present|current|till\s*date|\d{1,2}\/\d{4}|\d{4}-\d{2}|[a-z]+\s+\d{4}|\d{4}))\s*(\S.*)?$/i
  );
  if (!dateEndMatch) return { company: t };
  const companyPart = dateEndMatch[1].trim();
  const afterDate = dateEndMatch[2]?.trim();
  if (!afterDate || afterDate.length > 90) return { company: t };
  if (/^[-•·*►>]\s/.test(afterDate)) return { company: t };
  return { company: companyPart, role: afterDate };
}

function classifyWELine(line: string, prevType: WELineType | null): WELineType {
  const t = line.trim();
  if (!t) return 'body';
  if (/^[-•·*►>]\s/.test(t)) return 'bullet';
  if (hasDateRange(t) || hasPipeSeparator(t)) return 'company';
  // Role title: short line right after company/role header, not a sentence ending in .!?
  if (
    (prevType === 'company' || prevType === 'role') &&
    t.length < 110 &&
    !/[.!?]$/.test(t) &&
    !/^(the|and|or|with|for|to|of|in|on|at)\s/i.test(t)
  ) return 'role';
  return 'body';
}

/** Sections that get Work-Experience-style line classification + auto-bullets. */
const WE_SECTIONS = new Set(['Work Experience', 'Professional Experience', 'Employment History', 'Projects']);

/** Approximate cap-height above baseline (mm) for jsPDF text placement */
function textAscenderMm(fontSizePt: number): number {
  return fontSizePt * 0.352778 * 0.72;
}

/** Y baseline for the first content line after a section header block */
function contentBaselineAfterHeader(headerBottomY: number, bodyFontPt: number, gapMm = 2.5): number {
  return headerBottomY + gapMm + textAscenderMm(bodyFontPt);
}

/** Wrap text for PDF, preferring breaks at pipe separators to keep date ranges intact. */
function wrapResumeLine(doc: jsPDF, line: string, maxWidth: number): string[] {
  if (doc.getTextWidth(line) <= maxWidth) return [line];

  const pipeSep = line.includes(' || ') ? ' || ' : line.includes(' | ') ? ' | ' : null;
  if (pipeSep) {
    const idx = line.indexOf(pipeSep);
    const head = line.slice(0, idx).trim();
    const tail = line.slice(idx + pipeSep.length).trim();
    const out: string[] = [];
    if (head) out.push(...doc.splitTextToSize(head, maxWidth));
    if (tail) {
      if (doc.getTextWidth(tail) <= maxWidth) out.push(tail);
      else out.push(...doc.splitTextToSize(tail, maxWidth));
    }
    return out.length ? out : doc.splitTextToSize(line, maxWidth);
  }

  return doc.splitTextToSize(line, maxWidth);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function textToSections(text: string): ResumeSection[] {
  return sortSectionsByCanonicalOrder(
    normalizeSectionTitles(parseResumeSections(normalizeResumeText(text)))
  );
}

/** Lines that repeat a section header inside PI content — skip during name extraction. */
const SECTION_TITLE_RE = /^(personal\s*info|contact\s*info?|contact\s*details?|summary|profile|objective|experience|education|skills|work|professional|employment|resume|curriculum\s*vitae|references)\b/i;

/** True when a string looks like a contact marker (email, URL, phone, LinkedIn). */
function isContactMarker(t: string): boolean {
  return /@|http|linkedin/i.test(t) || /\+?\d[\d\s\-().]{5,}/.test(t);
}

/** True when a string looks like a pure human name (no contact noise). */
function looksLikeName(t: string): boolean {
  return (
    t.length > 1 &&
    t.length < 55 &&
    !isContactMarker(t) &&
    !/phone|email|linkedin/i.test(t) &&
    !SECTION_TITLE_RE.test(t)
  );
}

function extractName(sections: ResumeSection[]): string {
  const pi = sections.find(s => /personal\s*info|contact/i.test(s.title));
  if (!pi) return 'Your Name';

  const rawLines = pi.content.split('\n').map(l => l.trim()).filter(Boolean);

  // Pass 1: find a pure-name line (no contact markers, not a section title)
  const pureName = rawLines.find(looksLikeName);
  if (pureName) return pureName;

  // Pass 2: all data is on one line — extract the first comma-delimited token that
  // looks like a name (e.g. "Dharmendra Singh, email, phone, url")
  for (const line of rawLines) {
    if (!isContactMarker(line)) continue;
    for (const part of line.split(/\s*,\s*/)) {
      const p = part.trim();
      if (looksLikeName(p)) return p;
    }
  }

  return 'Your Name';
}

function extractContactParts(sections: ResumeSection[]): string[] {
  const pi = sections.find(s => /personal\s*info|contact/i.test(s.title));
  if (!pi) return [];
  const name = extractName(sections).toLowerCase();

  const result: string[] = [];

  for (const raw of pi.content.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (SECTION_TITLE_RE.test(line)) continue;

    const isContact = /@|http|linkedin|phone|\+?\d[\d\s\-().]{6,}|^\+\d/i.test(line);
    const isShort   = line.length < 80;

    if (!isContact && !isShort) continue;

    // If the line bundles multiple contact items (e.g. "email, phone, url, linkedin"),
    // split on commas and emit each qualifying token individually.
    const isMultiContact =
      (/@/.test(line) && /\+?\d[\d\s\-().]{5,}/.test(line)) ||
      (/@/.test(line) && /https?:/i.test(line));

    if (isMultiContact) {
      for (const part of line.split(/\s*,\s*/)) {
        const p = part.trim();
        if (!p || p.length < 3) continue;
        if (name === p.toLowerCase()) continue;
        if (/@|http|linkedin|\+?\d[\d\s\-().]{5,}|^\+\d/i.test(p)) {
          if (result.length < 4) result.push(p);
        }
      }
    } else {
      if (!name.includes(line.toLowerCase()) && result.length < 4) {
        result.push(line);
      }
    }
  }

  return result;
}

function bulletText(line: string): string {
  return line.replace(/^[-•·*►>]\s+/, '');
}

/**
 * Strips common markdown tokens that the LLM sometimes leaves in generated text:
 * **bold**, *italic*, __bold__, _italic_, `code`, ~~strike~~.
 * Keeps the inner text content intact.
 */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')  // **bold**
    .replace(/\*([^*]+)\*/g, '$1')       // *italic*
    .replace(/__([^_]+)__/g, '$1')       // __bold__
    .replace(/_([^_]+)_/g, '$1')         // _italic_
    .replace(/`([^`]+)`/g, '$1')         // `code`
    .replace(/~~([^~]+)~~/g, '$1');      // ~~strike~~
}

/** Remove bracket placeholders and collapse excessive blank lines before export. */
function stripExportPlaceholders(content: string): string {
  return (content ?? '')
    .replace(/\[[^\]]{3,80}\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

/** Join line-broken summary paragraphs into one readable block. */
function collapseSummaryForExport(content: string): string {
  const lines = stripExportPlaceholders(content).split('\n').map((l) => l.trim()).filter(Boolean);
  const bullets = lines.filter((l) => /^[-•·*►>]\s/.test(l));
  const prose = lines.filter((l) => !/^[-•·*►>]\s/.test(l)).join(' ').replace(/\s{2,}/g, ' ');
  return [prose, ...bullets].filter(Boolean).join('\n');
}

/**
 * Merges hard-wrapped bullet continuation lines back onto their parent bullet.
 *
 * When the LLM produces a bullet that wraps across multiple lines in the raw
 * stored text, e.g.:
 *   "• Delivered consulting services to clients, providing\ntechnical\nexpertise\nand guidance"
 * each fragment becomes a spurious separate bullet in the document.
 *
 * A line is treated as a continuation of the previous line when ALL of:
 *  1. It does NOT start with a bullet marker (•-*►>) → not a new bullet
 *  2. It does NOT contain a date range            → not a company/header line
 *  3. It does NOT match a pipe-separated header   → not a "Role | Company" line
 *  4. The previous non-empty line ends WITHOUT sentence-ending punctuation
 *     (i.e., the previous line is incomplete mid-sentence)
 *  5. AND at least one of:
 *       • starts with a lowercase letter
 *       • starts with a common connective / preposition
 *       • is very short (< 40 chars) and does not look like "Proper Noun" pattern
 */
function mergeBulletContinuations(lines: string[]): string[] {
  const CONNECTIVES =
    /^(and|or|to|of|the|in|on|at|for|with|by|but|nor|yet|so|also|as|from|a|an|is|are|was|were|not|no|its|their|this|that|these|those|which|who|whom|whose|where|when|while|although|including|such|like)\s/i;

  const result: string[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();

    // Preserve blank lines (they act as paragraph breaks)
    if (!line) {
      result.push('');
      continue;
    }

    // Lines that are always their own entries — never merge as continuation
    if (
      /^[-•·*►>]\s/.test(line) ||  // new bullet
      hasDateRange(line) ||          // company/date header
      hasPipeSeparator(line)         // "Role | Company" header
    ) {
      result.push(line);
      continue;
    }

    // Check if this looks like a continuation of the last recorded line
    if (result.length > 0) {
      const prev = result[result.length - 1];

      if (prev !== '') {
        const prevEndsIncomplete = !/[.!?]$/.test(prev);
        const prevIsHeader = hasDateRange(prev) || hasPipeSeparator(prev);
        const prevEndsWithDate = /\b(present|current|till\s*date|\d{4})$/i.test(prev);
        if (prevEndsWithDate) {
          result.push(line);
          continue;
        }
        if (prevEndsIncomplete && !prevIsHeader) {
          const startsLower    = /^[a-z]/.test(line);
          const startsConnect  = CONNECTIVES.test(line);
          // Short fragment that doesn't look like "Proper Noun Proper Noun" (role title)
          const isShortFrag    = line.length < 40 && !/^[A-Z][a-z]+ [A-Z]/.test(line);

          if (startsLower || startsConnect || isShortFrag) {
            result[result.length - 1] = prev + ' ' + line;
            continue;
          }
        }
      }
    }

    result.push(line);
  }

  return result;
}

// ─── Preview-matched PDF generator (html2canvas approach) ─────────────────────
// Produces a PDF that looks exactly like the in-app resume preview by
// rendering the same HTML structure and capturing it with html2canvas.

/** Escapes a string for safe injection as HTML text content */
function escHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Converts a section's content lines to HTML matching the preview renderer */
function sectionContentToHtml(content: string): string {
  const lines = mergeBulletContinuations(content.split('\n'));
  return lines
    .map(raw => {
      const line = raw.trim();
      if (!line) return '<div style="height:4px;"></div>';

      // Bullet line
      if (/^[-•·*►>]\s/.test(line)) {
        const text = line.replace(/^[-•·*►>]\s+/, '');
        return `<div style="display:flex;gap:7px;margin:1px 0 1px 6px;align-items:baseline;">
          <span style="color:#4f46e5;font-weight:700;flex-shrink:0;">•</span>
          <span style="flex:1;">${escHtml(text)}</span>
        </div>`;
      }
      // Company / date header line
      if (hasDateRange(line) || hasPipeSeparator(line)) {
        return `<div style="font-weight:700;font-size:10.5pt;margin:10px 0 1px 0;">${escHtml(line)}</div>`;
      }
      // Plain body line
      return `<div style="margin:1px 0;">${escHtml(line)}</div>`;
    })
    .join('');
}

/** Builds the same clean HTML layout as the in-app OptimizedResumeContent preview */
function buildResumePreviewHTML(allSections: ResumeSection[]): string {
  const name = extractName(allSections);
  const contactParts = extractContactParts(allSections);

  const bodySections = allSections.filter(
    s => !/personal\s*info|contact/i.test(s.title) && s.content.trim(),
  );

  const sectionsHtml = bodySections
    .map(s => `
      <div style="margin-bottom:14px;">
        <div style="
          font-size:10pt;font-weight:700;color:#111827;
          letter-spacing:0.06em;text-transform:uppercase;
          border-bottom:1.5px solid #4f46e5;
          padding-bottom:3px;margin-bottom:6px;
        ">${escHtml(s.title.toUpperCase())}</div>
        ${sectionContentToHtml(s.content)}
      </div>
    `)
    .join('');

  return `
    <div style="
      font-family:Lexend,'Segoe UI',Arial,'Helvetica Neue',sans-serif;
      font-size:10pt;line-height:1.6;color:#1f2937;
      background:#ffffff;width:754px;padding:40px 48px;box-sizing:border-box;
    ">
      <div style="text-align:center;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #4f46e5;">
        <div style="font-size:20pt;font-weight:700;color:#111827;margin-bottom:4px;">${escHtml(name)}</div>
        <div style="font-size:9pt;color:#6b7280;">${contactParts.map(escHtml).join('  •  ')}</div>
      </div>
      ${sectionsHtml}
    </div>`;
}

/**
 * Generates a PDF that looks exactly like the in-app resume preview.
 * Renders the resume as clean HTML (same layout/content as the preview panel)
 * then captures it with html2canvas → jsPDF, with proper A4 multi-page support.
 * This replaces the old template-based PDF generator for resume downloads.
 */
export async function generateResumePDFPreview(
  resumeText: string,
  sections?: ResumeSection[],
): Promise<Blob> {
  const [{ default: html2canvas }, { jsPDF: JsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const allSections = sections?.length
    ? sortSectionsByCanonicalOrder(normalizeSectionTitles(sections))
    : textToSections(resumeText);

  const html = buildResumePreviewHTML(allSections);

  // Inject into a hidden off-screen container so html2canvas can capture it
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:fixed;left:-9999px;top:0;background:#fff;z-index:-1;overflow:visible;';
  wrapper.innerHTML = html;
  document.body.appendChild(wrapper);

  try {
    // Give the browser a tick to finish layout/render
    await new Promise<void>(resolve => setTimeout(resolve, 120));

    const canvas = await html2canvas(wrapper.firstElementChild as HTMLElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const A4_W_MM = 210;
    const A4_H_MM = 297;
    // A4 height in canvas pixels (at scale 2, width = 754*2 = 1508 px)
    const a4HeightPx = Math.round((A4_H_MM / A4_W_MM) * canvas.width);

    const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const totalPages = Math.ceil(canvas.height / a4HeightPx);

    for (let p = 0; p < totalPages; p++) {
      if (p > 0) pdf.addPage();

      const srcY = p * a4HeightPx;
      const srcH = Math.min(a4HeightPx, canvas.height - srcY);

      // Slice the full canvas to this page's region
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = a4HeightPx;
      const ctx = pageCanvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

      const imgData = pageCanvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, A4_W_MM, A4_H_MM);
    }

    return pdf.output('blob');
  } finally {
    document.body.removeChild(wrapper);
  }
}

// ─── PDF generator (template-styled, kept for template preview use) ───────────

export async function generateResumePDF(
  resumeText: string,
  sections?: ResumeSection[],
  jobTitle = '',
  templateId: TemplateId = DEFAULT_TEMPLATE,
): Promise<Blob> {
  const palette = PALETTES[templateId] ?? PALETTES[DEFAULT_TEMPLATE];
  const allSections = sections?.length
    ? sortSectionsByCanonicalOrder(normalizeSectionTitles(sections))
    : textToSections(resumeText);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PW   = 210;
  const PH   = 297;
  const BAR_W = palette.accentBar ? 4 : 0;
  // ~0.7 in side margins — standard ATS-friendly resume layout on A4
  const ML   = BAR_W + 18;
  const MR   = 18;
  const MT   = 18;
  const MB   = 16;
  const CW   = PW - ML - MR;

  // ── Font sizes (pt) ─── heading MUST be larger than body ──────────────────
  const FS_NAME    = 20;
  const FS_JOBTITLE = 9.5;
  const FS_CONTACT  = 8.5;
  const FS_SEC_HDR  = 10.5;
  const FS_COMPANY  = 10;
  const FS_ROLE     = 9.5;
  const FS_BODY     = 9.5;

  const LH_BODY  = FS_BODY  * 0.55;
  const LH_SMALL = FS_CONTACT * 0.55;

  let y = MT;

  const SAFE_BOTTOM = PH - MB - 5;

  const newPage = () => { doc.addPage(); y = MT; };
  const ensureSpace = (n: number) => { if (y + n >= SAFE_BOTTOM) newPage(); };
  /** Keep section title with at least ~2 body lines on the same page. */
  const ensureSectionStart = (headerBlock = 16, minBody = 12) => {
    if (y + headerBlock + minBody >= SAFE_BOTTOM) newPage();
  };
  const setC = (c: RGB) => doc.setTextColor(c.r, c.g, c.b);
  const setD = (c: RGB) => doc.setDrawColor(c.r, c.g, c.b);
  const setF = (c: RGB) => doc.setFillColor(c.r, c.g, c.b);
  const font = palette.font;

  // ── Left accent bar ────────────────────────────────────────────────────────
  if (palette.accentBar) {
    setF(palette.accentBar);
    doc.rect(0, 0, BAR_W, PH, 'F');
  }

  // ── Name ───────────────────────────────────────────────────────────────────
  const name = extractName(allSections);
  const contactParts = extractContactParts(allSections);
  const cx = palette.nameAlign === 'center' ? PW / 2 : ML;
  const cOpt = palette.nameAlign === 'center' ? ({ align: 'center' } as const) : undefined;

  doc.setFont(font, 'bold');
  doc.setFontSize(FS_NAME);
  setC(palette.nameColor);
  doc.text(name, cx, y + 6, cOpt);
  y += 10;

  if (jobTitle) {
    doc.setFont(font, 'normal');
    doc.setFontSize(FS_JOBTITLE);
    setC(palette.roleTitleColor);
    doc.text(jobTitle.toUpperCase(), cx, y, cOpt);
    y += 5;
  }

  if (contactParts.length) {
    doc.setFont(font, 'normal');
    doc.setFontSize(FS_CONTACT);
    // Muted contact line
    doc.setTextColor(
      Math.min(255, palette.bodyText.r + 70),
      Math.min(255, palette.bodyText.g + 70),
      Math.min(255, palette.bodyText.b + 70),
    );
    const contactStr = contactParts.join('  |  ');
    const wrapped = doc.splitTextToSize(contactStr, CW);
    doc.text(wrapped, cx, y, cOpt);
    y += wrapped.length * LH_SMALL * 1.6 + 2;
  }

  setD(palette.dividerColor);
  doc.setLineWidth(0.5);
  doc.line(ML, y, PW - MR, y);
  y += 4;

  // ── Body sections ──────────────────────────────────────────────────────────
  for (const bucket of BODY_BUCKETS) {
    const section = allSections.find(s => s.title === bucket);
    if (!section?.content.trim()) continue;

    ensureSectionStart();

    // ── Section heading — light tint bar matching on-screen preview ──
    if (palette.sectionHeaderStyle === 'filled-bar') {
      const headerH = 7.5;
      if (y > MT + 2) y += 2;
      const barTop = y;
      const fillBg = palette.sectionHeaderFillBg ?? rgb(243, 244, 246);
      setF(fillBg);
      doc.rect(ML, barTop, CW, headerH, 'F');
      setD(palette.headerBorderColor);
      doc.setLineWidth(0.35);
      doc.line(ML, barTop + headerH, ML + CW, barTop + headerH);
      doc.setFont(font, 'bold');
      doc.setFontSize(FS_SEC_HDR);
      setC(palette.sectionTitleColor);
      // Baseline inside bar — jsPDF y is baseline, not top
      doc.text(bucket.toUpperCase(), ML + 2.5, barTop + headerH - 1.8);
      y = contentBaselineAfterHeader(barTop + headerH, FS_BODY);
    } else if (palette.sectionHeaderStyle === 'text-underline') {
      if (y > MT + 2) y += 2;
      doc.setFont(font, 'bold');
      doc.setFontSize(FS_SEC_HDR);
      setC(palette.sectionTitleColor);
      doc.text(bucket.toUpperCase(), ML, y);
      setD(palette.headerBorderColor);
      doc.setLineWidth(0.45);
      const ruleY = y + 1.2;
      doc.line(ML, ruleY, PW - MR, ruleY);
      y = contentBaselineAfterHeader(ruleY + 0.4, FS_BODY);
    } else {
      // text-double-underline (serif / mono)
      if (y > MT + 2) y += 2;
      doc.setFont(font, 'bold');
      doc.setFontSize(FS_SEC_HDR);
      setC(palette.sectionTitleColor);
      doc.text(bucket.toUpperCase(), ML, y);
      setD(palette.headerBorderColor);
      doc.setLineWidth(0.5);
      doc.line(ML, y + 1.5, PW - MR, y + 1.5);
      doc.setLineWidth(0.2);
      doc.line(ML, y + 2.8, PW - MR, y + 2.8);
      y = contentBaselineAfterHeader(y + 3.2, FS_BODY);
    }

    // ── Section content ──
    const isWESection = WE_SECTIONS.has(bucket);
    let sectionBody = stripExportPlaceholders(section.content);
    if (bucket.toLowerCase().includes('summary')) {
      sectionBody = collapseSummaryForExport(sectionBody);
    }
    const rawLines = mergeBulletContinuations(sectionBody.split('\n'));
    let prevType: WELineType | null = null;
    let prevLineWasCompany = false;

    for (let li = 0; li < rawLines.length; li++) {
      const rawLine = rawLines[li];
      const line = stripMarkdown(rawLine.trim());
      if (!line) { prevLineWasCompany = false; continue; }

      if (isWESection) {
        const lineType = classifyWELine(line, prevType);
        prevType = lineType;

        if (lineType === 'company') {
          const { company: companyPart, role: rolePart } = splitCompanyDateAndRole(line);
          if (!prevLineWasCompany && li > 0) y += 3;
          ensureSpace(9);
          doc.setFont(font, 'bold');
          doc.setFontSize(FS_COMPANY);
          setC(palette.companyHeaderColor);
          const wrappedCompany = wrapResumeLine(doc, companyPart, CW);
          doc.text(wrappedCompany, ML, y);
          y += wrappedCompany.length * (LH_BODY * 1.1) + 1;
          prevLineWasCompany = true;
          if (rolePart) {
            ensureSpace(6);
            doc.setFont(font, 'italic');
            doc.setFontSize(FS_ROLE);
            setC(palette.roleTitleColor);
            const wrappedRole = wrapResumeLine(doc, rolePart, CW);
            doc.text(wrappedRole, ML, y);
            y += wrappedRole.length * LH_BODY + 1.5;
            prevType = 'role';
          }
        } else if (lineType === 'role') {
          const wrappedRole = wrapResumeLine(doc, line, CW);
          ensureSpace(wrappedRole.length * LH_BODY + LH_BODY);
          doc.setFont(font, 'italic');
          doc.setFontSize(FS_ROLE);
          setC(palette.roleTitleColor);
          doc.text(wrappedRole, ML, y);
          y += wrappedRole.length * LH_BODY + 1.5;
          prevLineWasCompany = false;

        } else if (lineType === 'bullet') {
          doc.setFont(font, 'normal');
          doc.setFontSize(FS_BODY);
          setC(palette.bodyText);
          const text = bulletText(line);
          const wrapped = doc.splitTextToSize(text, CW - 9);
          ensureSpace(wrapped.length * LH_BODY + 2);
          setC(palette.bulletColor);
          doc.text('•', ML + 3, y);
          setC(palette.bodyText);
          doc.text(wrapped, ML + 9, y);
          y += wrapped.length * LH_BODY + 0.8;
          prevLineWasCompany = false;

        } else {
          // body — auto-bullet responsibility lines
          doc.setFont(font, 'normal');
          doc.setFontSize(FS_BODY);
          setC(palette.bodyText);
          const wrapped = doc.splitTextToSize(line, CW - 7);
          ensureSpace(wrapped.length * LH_BODY + 2);
          setC(palette.bulletColor);
          doc.text('•', ML + 2, y);
          setC(palette.bodyText);
          doc.text(wrapped, ML + 7, y);
          y += wrapped.length * LH_BODY + 0.6;
          prevLineWasCompany = false;
        }
      } else {
        // Non-WE section: plain body text with bullet if line starts with bullet char
        const isBullet = /^[-•·*►>]\s/.test(line);
        if (isBullet) {
          doc.setFont(font, 'normal');
          doc.setFontSize(FS_BODY);
          setC(palette.bodyText);
          const text = bulletText(line);
          const wrapped = doc.splitTextToSize(text, CW - 9);
          ensureSpace(wrapped.length * LH_BODY + 2);
          setC(palette.bulletColor);
          doc.text('•', ML + 3, y);
          setC(palette.bodyText);
          doc.text(wrapped, ML + 9, y);
          y += wrapped.length * LH_BODY + 0.6;
        } else if (hasDateRange(line) || hasPipeSeparator(line)) {
          ensureSpace(9);
          doc.setFont(font, 'bold');
          doc.setFontSize(FS_COMPANY);
          setC(palette.companyHeaderColor);
          const wrapped = wrapResumeLine(doc, line, CW);
          doc.text(wrapped, ML, y);
          y += wrapped.length * (LH_BODY * 1.1) + 1;
        } else {
          doc.setFont(font, 'normal');
          doc.setFontSize(FS_BODY);
          setC(palette.bodyText);
          const wrapped = doc.splitTextToSize(line, CW);
          ensureSpace(wrapped.length * LH_BODY + 2);
          doc.text(wrapped, ML, y);
          y += wrapped.length * LH_BODY + 0.4;
        }
      }
    }

    y += 3;
  }

  return doc.output('blob');
}

// ─── DOCX generator ──────────────────────────────────────────────────────────

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
   .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

// ── DOCX font sizes (half-points: 1pt = 2 units) ─────────────────────────────
// Name:        48 hp = 24pt
// Section hdr: 28 hp = 14pt   ← LARGER than body (was 20hp = 10pt, now fixed)
// Company hdr: 24 hp = 12pt   ← bold
// Role title:  22 hp = 11pt   ← italic
// Body text:   20 hp = 10pt   ← was 18hp = 9pt, now 10pt (more readable)

function para(
  text: string,
  opts: {
    bold?: boolean;
    italic?: boolean;
    size?: number;
    color?: string;
    align?: 'left' | 'center' | 'right';
    spaceBefore?: number;
    spaceAfter?: number;
    indent?: number;
    borderBottom?: string;
    borderStyle?: 'single' | 'double';
    fontName?: string;
    shading?: string;
  } = {},
): string {
  const sz = opts.size ?? 20;
  const fName = opts.fontName ?? DOCX_FONT_SANS;
  const rPr = [
    opts.bold   ? '<w:b/>'   : '',
    opts.italic ? '<w:i/>'   : '',
    `<w:sz w:val="${sz}"/>`,
    `<w:szCs w:val="${sz}"/>`,
    opts.color  ? `<w:color w:val="${opts.color}"/>` : '',
    `<w:rFonts w:ascii="${fName}" w:hAnsi="${fName}" w:cs="${fName}"/>`,
  ].filter(Boolean).join('');

  const borderXml = opts.borderBottom
    ? `<w:pBdr><w:bottom w:val="${opts.borderStyle ?? 'single'}" w:sz="6" w:space="1" w:color="${opts.borderBottom}"/></w:pBdr>`
    : '';

  const shadingXml = opts.shading
    ? `<w:shd w:val="clear" w:color="auto" w:fill="${opts.shading}"/>`
    : '';

  const pPrParts = [
    opts.align && opts.align !== 'left' ? `<w:jc w:val="${opts.align}"/>` : '',
    `<w:spacing w:before="${opts.spaceBefore ?? 0}" w:after="${opts.spaceAfter ?? 60}"/>`,
    opts.indent ? `<w:ind w:left="${opts.indent}"/>` : '',
    borderXml,
    shadingXml,
  ].filter(Boolean).join('');

  return `<w:p>${pPrParts ? `<w:pPr>${pPrParts}</w:pPr>` : ''}<w:r><w:rPr>${rPr}</w:rPr><w:t xml:space="preserve">${esc(text)}</w:t></w:r></w:p>`;
}

function sectionHeaderPara(bucket: string, palette: TemplatePalette): string {
  const fName = palette.docxFont;
  const SIZE = 28; // 14pt — clearly larger than 10pt body text
  const rPr = [
    '<w:b/>',
    `<w:sz w:val="${SIZE}"/>`,
    `<w:szCs w:val="${SIZE}"/>`,
    `<w:color w:val="${palette.docxHeaderTextHex}"/>`,
    `<w:rFonts w:ascii="${fName}" w:hAnsi="${fName}" w:cs="${fName}"/>`,
  ].join('');

  if (palette.docxHeaderBgHex) {
    return `<w:p>
      <w:pPr>
        <w:spacing w:before="200" w:after="100"/>
        <w:shd w:val="clear" w:color="auto" w:fill="${palette.docxHeaderBgHex}"/>
        <w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="${palette.docxAccentHex}"/></w:pBdr>
      </w:pPr>
      <w:r><w:rPr>${rPr}</w:rPr><w:t>${esc(bucket.toUpperCase())}</w:t></w:r>
    </w:p>`;
  }

  if (palette.sectionHeaderStyle === 'text-double-underline') {
    const rPr2 = rPr.replace(palette.docxHeaderTextHex, palette.docxAccentHex);
    return `<w:p>
      <w:pPr>
        <w:spacing w:before="240" w:after="100"/>
        <w:pBdr><w:bottom w:val="double" w:sz="6" w:space="1" w:color="${palette.docxAccentHex}"/></w:pBdr>
      </w:pPr>
      <w:r><w:rPr>${rPr2}</w:rPr><w:t>${esc(bucket.toUpperCase())}</w:t></w:r>
    </w:p>`;
  }

  const rPr3 = rPr.replace(palette.docxHeaderTextHex, palette.docxAccentHex);
  return `<w:p>
    <w:pPr>
      <w:spacing w:before="240" w:after="100"/>
      <w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="${palette.docxAccentHex}"/></w:pBdr>
    </w:pPr>
    <w:r><w:rPr>${rPr3}</w:rPr><w:t>${esc(bucket.toUpperCase())}</w:t></w:r>
  </w:p>`;
}

export async function generateResumeDOCX(
  resumeText: string,
  sections?: ResumeSection[],
  jobTitle = '',
  templateId: TemplateId = DEFAULT_TEMPLATE,
): Promise<Blob> {
  const palette = PALETTES[templateId] ?? PALETTES[DEFAULT_TEMPLATE];
  const allSections = sections?.length
    ? sortSectionsByCanonicalOrder(normalizeSectionTitles(sections))
    : textToSections(resumeText);

  const name = extractName(allSections);
  const contactParts = extractContactParts(allSections);
  const fName = palette.docxFont;
  // Always use true-black for body text so it's readable regardless of template
  const BODY_COLOR = '111827';

  const paras: string[] = [];

  // Name — 24pt bold
  paras.push(para(name, {
    bold: true, size: 48, color: BODY_COLOR,
    align: palette.nameAlign, spaceAfter: 80, fontName: fName,
  }));

  // Job title — 11pt accent
  if (jobTitle) {
    paras.push(para(jobTitle.toUpperCase(), {
      bold: true, size: 22, color: palette.docxAccentHex,
      align: palette.nameAlign, spaceAfter: 80, fontName: fName,
    }));
  }

  // Contact — 9pt muted
  if (contactParts.length) {
    paras.push(para(contactParts.join('   |   '), {
      size: 18, color: 'A0A0AA',
      align: palette.nameAlign, spaceAfter: 120, fontName: fName,
    }));
  }

  // Divider (sz="4" = 0.5pt — slim under-name rule)
  paras.push(
    `<w:p><w:pPr><w:spacing w:before="0" w:after="160"/>` +
    `<w:pBdr><w:bottom w:val="single" w:sz="4" w:space="1" w:color="${palette.docxAccentHex}"/></w:pBdr>` +
    `</w:pPr></w:p>`,
  );

  // Body sections
  for (const bucket of BODY_BUCKETS) {
    const section = allSections.find(s => s.title === bucket);
    if (!section?.content.trim()) continue;

    paras.push(sectionHeaderPara(bucket, palette));

    const isWESection = WE_SECTIONS.has(bucket);
    let sectionBody = stripExportPlaceholders(section.content);
    if (bucket.toLowerCase().includes('summary')) {
      sectionBody = collapseSummaryForExport(sectionBody);
    }
    const rawLines = mergeBulletContinuations(sectionBody.split('\n'));
    let prevType: WELineType | null = null;
    let firstEntry = true;

    for (const rawLine of rawLines) {
      const line = stripMarkdown(rawLine.trim());
      if (!line) continue;

      if (isWESection) {
        const lineType = classifyWELine(line, prevType);
        prevType = lineType;

        if (lineType === 'company') {
          paras.push(para(line, {
            bold: true, size: 24, color: BODY_COLOR,
            spaceBefore: firstEntry ? 60 : 240,
            spaceAfter: 40, fontName: fName,
          }));
          firstEntry = false;

        } else if (lineType === 'role') {
          paras.push(para(line, {
            italic: true, size: 22, color: BODY_COLOR,
            spaceBefore: 0, spaceAfter: 60, fontName: fName,
          }));

        } else if (lineType === 'bullet') {
          paras.push(para('• ' + bulletText(line), {
            size: 20, color: BODY_COLOR,
            indent: 360, spaceBefore: 0, spaceAfter: 40, fontName: fName,
          }));

        } else {
          paras.push(para('• ' + line, {
            size: 20, color: BODY_COLOR,
            indent: 360, spaceBefore: 0, spaceAfter: 40, fontName: fName,
          }));
        }
      } else {
        const isBullet = /^[-•·*►>]\s/.test(line);
        if (isBullet) {
          paras.push(para('• ' + bulletText(line), {
            size: 20, color: BODY_COLOR,
            indent: 360, spaceAfter: 40, fontName: fName,
          }));
        } else if (hasDateRange(line) || hasPipeSeparator(line)) {
          paras.push(para(line, {
            bold: true, size: 24, color: BODY_COLOR,
            spaceBefore: 160, spaceAfter: 40, fontName: fName,
          }));
        } else {
          paras.push(para(line, {
            size: 20, color: BODY_COLOR,
            spaceAfter: 40, fontName: fName,
          }));
        }
      }
    }
  }

  // ── OOXML structure ────────────────────────────────────────────────────────

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${paras.join('\n')}
    <w:sectPr>
      <!-- A4: 210mm x 297mm = 11906 x 16838 twips (1 twip = 1/1440 inch) -->
      <w:pgSz w:w="11906" w:h="16838"/>
      <!-- 1 inch = 1440 twips — standard professional resume margins -->
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"
               w:header="720" w:footer="720" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  const embedLexend = usesEmbeddedLexend(fName);
  let lexendRegularOdttf: Uint8Array | null = null;
  let lexendBoldOdttf: Uint8Array | null = null;
  if (embedLexend) {
    const { regular, bold } = await loadLexendFontBytes();
    lexendRegularOdttf = obfuscateFontForDocx(regular, LEXEND_EMBED_REGULAR_GUID);
    lexendBoldOdttf = obfuscateFontForDocx(bold, LEXEND_EMBED_BOLD_GUID);
  }

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  ${embedLexend ? '<Default Extension="odttf" ContentType="application/vnd.openxmlformats-officedocument.obfuscatedFont"/>' : ''}
  <Override PartName="/word/document.xml"
    ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml"
    ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  ${embedLexend ? '<Override PartName="/word/fontTable.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.fontTable+xml"/>' : ''}
</Types>`;

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
    Target="word/document.xml"/>
</Relationships>`;

  const wordRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles"
    Target="styles.xml"/>
  ${embedLexend ? `<Relationship Id="rId2"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/fontTable"
    Target="fontTable.xml"/>` : ''}
</Relationships>`;

  const fontTableXml = embedLexend ? `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:fonts xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
         xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:font w:name="${DOCX_FONT_SANS}">
    <w:altName w:val="Calibri"/>
    <w:panose1 w:val="020B0504020204030204"/>
    <w:charset w:val="4B"/>
    <w:family w:val="swiss"/>
    <w:pitch w:val="variable"/>
    <w:embedRegular r:id="rId1" w:fontKey="${LEXEND_EMBED_REGULAR_GUID}"/>
    <w:embedBold r:id="rId2" w:fontKey="${LEXEND_EMBED_BOLD_GUID}"/>
  </w:font>
</w:fonts>` : '';

  const fontTableRelsXml = embedLexend ? `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/font"
    Target="fonts/lexend-regular.odttf"/>
  <Relationship Id="rId2"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/font"
    Target="fonts/lexend-bold.odttf"/>
</Relationships>` : '';

  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>
        <w:rFonts w:ascii="${fName}" w:hAnsi="${fName}" w:cs="${fName}"/>
        <w:sz w:val="20"/>
        <w:szCs w:val="20"/>
        <w:color w:val="111827"/>
        <w:lang w:val="en-US"/>
      </w:rPr>
    </w:rPrDefault>
  </w:docDefaults>
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:pPr><w:spacing w:after="60"/></w:pPr>
    <w:rPr>
      <w:color w:val="111827"/>
      <w:sz w:val="20"/>
      <w:szCs w:val="20"/>
    </w:rPr>
  </w:style>
</w:styles>`;

  const zip = new JSZip();
  zip.file('[Content_Types].xml', contentTypesXml);
  zip.file('_rels/.rels', relsXml);
  zip.file('word/document.xml', documentXml);
  zip.file('word/_rels/document.xml.rels', wordRelsXml);
  zip.file('word/styles.xml', stylesXml);
  if (embedLexend && lexendRegularOdttf && lexendBoldOdttf) {
    zip.file('word/fontTable.xml', fontTableXml);
    zip.file('word/_rels/fontTable.xml.rels', fontTableRelsXml);
    zip.file('word/fonts/lexend-regular.odttf', lexendRegularOdttf);
    zip.file('word/fonts/lexend-bold.odttf', lexendBoldOdttf);
  }

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}
